import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Grid, Group,
  Paper,
  Progress,
  Text,
  Title
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { Location, Navigate, useLocation, useNavigate } from "react-router-dom";
import InputField, { InputFieldTypes } from "../components/input_field";
import { useAuth } from "../contexts/auth.context";
import { useLanguage } from "../contexts/language.context";
import { api } from "../services/api";
import { SUBSCRIBE } from "../services/auth";
import { SEARCH_COUPON } from "../services/companies";
import { phoneCountryCodes, useQuery } from "../utility/util";

export default function Signup({ shareLinkId = null, partner = false }) {
  const { user, signIn, companyHost, ip } = useAuth();
  const { str } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 900px)');
  const [selectedRecurrencyType, setSelectedRecurrencyType] = useState<string>("year");
  const [agree, setAgree] = useState<boolean>(false);

  const [formData, setFormData] = useState<any>({
    company: {
      type: "1",
      employees: "1"
    }
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [steps, setSteps] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any>({});

  const state = location.state as { from: Location };
  const query = useQuery();
  const queryFrom = query.get("from");
  const from = queryFrom ?? (state ? `${state.from.pathname}` : "/");

  const queryMail = query.get("email");
  const referral = query.get("referral") ?? "";

  const validTab = [
    () => formData?.general?.name &&
      formData?.general?.email &&
      // formData?.general?.register_number &&
      formData?.general?.phone &&
      formData?.general?.phone_code &&
      formData?.general?.password,
  ].filter(nn => nn);

  const signup = (others = {}) => {
    setLoading(true)

    api.post(
      `/auth/signup`,
      {
        ...formData,
      }
    )
      .then(({ data: { user } }) => {
        setLoading(false);

        signIn(user, () => navigate(from));
      })
      .catch(err => {
        setLoading(false);
        notifications.show({ title: "Ops", message: err.message, color: 'red' })
      })
  }

  const navigateToTab = (index: number) => {
    if (steps[index - 1]?.onNext) steps[index - 1]?.onNext();

    if (index === steps.length) signup();
    else setSelectedTab(index);
  }

  const handleChangeGeneral = (dt: { email: string | null; }) => setFormData((fd: { general: any; }) => ({ ...fd, general: { ...fd?.general, ...dt } }))

  useEffect(() => {
    if (companyHost?.fastGeneration) setFormData((fd: { general: any; company: any; }) => ({
      ...fd,
      general: {
        ...fd?.general,
        name: companyHost.name,
      },
      company: {
        ...fd?.company,
        name: companyHost.name,
      },
    }))
  }, [companyHost]);

  useEffect(() => {
    handleChangeGeneral({ email: queryMail })
  }, [queryMail]);

  const GeneralStep = <Box p="md">
    <InputField
      title={str("YOUR_NAME")}
      mb="lg"
      autoComplete="off"
      name="name"
      value={formData?.general?.name}
      onChange={handleChangeGeneral}
    />
    <InputField
      title={str("EMAIL")}
      mb="lg"
      name="email"
      autoCapitalize="off"
      value={formData?.general?.email}
      onChange={handleChangeGeneral}
    />
    <Group style={isMobile ? { display: 'block' } : {}}>
      <InputField
        style={isMobile ? {} : { flex: 0.3 }}
        title={str("COUNTRY")}
        mb="lg"
        name="phone_code"
        autoComplete="tel-country-code"
        fieldType={InputFieldTypes.SELECT}
        value={formData?.general?.phone_code}
        onChange={handleChangeGeneral}
        nothingFound="No country code found"
        options={phoneCountryCodes}
      />
      <InputField
        style={isMobile ? {} : { flex: 0.7 }}
        title={str("PHONE")}
        mb="lg"
        name="phone"
        value={formData?.general?.phone}
        onChange={handleChangeGeneral}
      />
    </Group>

    <InputField
      title={str("PASSWORD")}
      mb="lg"
      name="password"
      fieldType={InputFieldTypes.PASSWORD}
      value={formData?.general?.password}
      onChange={handleChangeGeneral}
    />
  </Box>;

  const activeForm: any = (
    [
      { principal: GeneralStep },
    ].filter(nn => nn)
  )[selectedTab];

  useEffect(() => {
    if (formData?.coupon?.length > 3) SEARCH_COUPON(formData?.coupon)
      .then(res => {
        setCoupons((c: any) => ({ ...c, [formData?.coupon]: res }))
      })
      .catch(err => {
      })
  }, [formData?.coupon]);

  useEffect(() => {
    setSteps([
      {
        title: str("SIGNUP.WELCOME_TITLE"), subtitle: str("SIGNUP.WELCOME_SUBTITLE"), onNext: () => {
          setFormData((fd: { general: { name: any; email: any; phone: any; phone_code: any; }; }) => {
            SUBSCRIBE({ referral, name: fd?.general?.name, email: fd?.general?.email, phone: fd?.general?.phone, phone_code: fd?.general?.phone_code })
              .then(() => { })
              .catch(() => { })
            return fd;
          })
        }
      },
    ])
    // }
  }, [])

  return !!user
    ? <Navigate to="/" state={{ from }} />
    : (
      <div style={{
        backgroundColor: '#014040',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        minHeight: '100vh',
        zIndex: 0,
        ...(
          isMobile
            ? {
              padding: 20,
            }
            : {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: activeForm?.secondary ? '10vh 10vw' : '10vh 20vw',
            }
        )
      }}>
        <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, left: 0, background: '#00000055', zIndex: -1 }}></div>
        <Paper p="md">
          {(isMobile && activeForm?.secondary)
            ? activeForm?.secondary
            : <Grid>
              <Grid.Col span={{ base: 12, md: activeForm?.secondary ? 7 : 12 }}>
                <div style={{ display: 'flex', margin: '40px 0 10px', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                  <img alt="kapo" src={companyHost?.image ? companyHost?.image : `/logo.png`} style={{ maxHeight: 60, maxWidth: '180px' }} />
                </div>

                <Title ta="center" mt="xl" order={2}>{steps[selectedTab]?.title}</Title>
                <Text ta="center" size="sm">{steps[selectedTab]?.subtitle}</Text>

                <Box style={{ padding: isMobile ? '20px 10px 20px 10px' : '40px 60px 40px 60px' }}>
                  {/* <Paper p="md" mb="md">
                    <Group style={{ padding: isMobile ? '0 4px' : '0 10px' }}>
                      <ActionIcon onClick={() => { if (selectedTab === 0) { navigate("/login") } else { setSelectedTab(i => i - 1) } }}><FaChevronLeft /></ActionIcon>
                      <Box style={{ flex: 1 }}>
                        <Progress
                          value={(((selectedTab ?? 0) + 1) / (steps.length)) * 100}
                          color="dark"
                        />
                      </Box>
                      <ActionIcon style={{ visibility: 'hidden' }}><FaChevronLeft /></ActionIcon>
                    </Group>
                  </Paper> */}

                  {/* <Box style={{padding: '0 0 40px 0'}}>
                            </Box> */}
                  <form onSubmit={e => { e.preventDefault(); navigateToTab(selectedTab + 1) }} style={{ overflowY: 'auto' }}>
                    {activeForm?.principal}
                    <Box mt="xl" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Button
                        type="submit"
                        loading={loading}
                        disabled={validTab[selectedTab] && !validTab[selectedTab]()}
                        color="green"
                        variant="filled"
                        size="md">{selectedTab + 1 === steps.length ? str("CONTINUE") : str("CONTINUE")}</Button>
                    </Box>
                    <Group mt="xl" mb="xl">
                      <Divider style={{ flex: 1, margin: '0 10px', borderColor: '#AFAFAF' }} />
                      <Text c="gray">{str("OR").toUpperCase()}</Text>
                      <Divider style={{ flex: 1, margin: '0 10px', borderColor: '#AFAFAF' }} />
                    </Group>
                    <Box mt="xl" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Button
                        loading={loading}
                        color="dark"
                        variant="outline"
                        onClick={() => navigate("/login")}
                        size="md">{str("LOGIN.LOGIN")}</Button>
                    </Box>
                  </form>
                </Box>
              </Grid.Col>
              {activeForm?.secondary && !isMobile && <Grid.Col span={{ base: 12, md: 5 }}>
                {activeForm.secondary}
              </Grid.Col>}
            </Grid>}
        </Paper>
      </div>
    );
};
